import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('topbar-filters', false);

// ── Standalone numeric range initializer (used by both topbar and drawer) ────

function createNumericRange(container) {
  if (typeof noUiSlider === 'undefined') {
    debug.warn('noUiSlider not available');
    return;
  }

  const sliderEl = container.querySelector('.numeric-range__slider');
  const inputMin = container.querySelector('.numeric-range__input--min');
  const inputMax = container.querySelector('.numeric-range__input--max');
  const displayMin = container.querySelector('.numeric-range__display-min');
  const displayMax = container.querySelector('.numeric-range__display-max');
  const checkboxes = container.querySelectorAll('.numeric-range__checkbox');

  if (!sliderEl) return;

  const min = parseFloat(container.dataset.min) || 0;
  const max = parseFloat(container.dataset.max) || 100;
  const minValue = isNaN(parseFloat(container.dataset.minValue)) ? min : parseFloat(container.dataset.minValue);
  const maxValue = isNaN(parseFloat(container.dataset.maxValue)) ? max : parseFloat(container.dataset.maxValue);

  if (sliderEl.noUiSlider) sliderEl.noUiSlider.destroy();

  const slider = noUiSlider.create(sliderEl, {
    connect: true,
    step: 1,
    start: [minValue, maxValue],
    range: { min, max },
  });

  slider.on('update', ([low, high]) => {
    const lo = Math.round(low);
    const hi = Math.round(high);
    if (inputMin) inputMin.value = lo;
    if (inputMax) inputMax.value = hi;
    if (displayMin) displayMin.textContent = lo;
    if (displayMax) displayMax.textContent = hi;
  });

  slider.on('change', ([low, high]) => {
    const lo = Math.round(low);
    const hi = Math.round(high);

    let triggerLi = null;
    checkboxes.forEach(cb => {
      const val = parseFloat(cb.dataset.numValue);
      cb.checked = val >= lo && val <= hi;
      if (!triggerLi && cb.checked) {
        triggerLi = cb.closest('li');
      }
    });

    if (!triggerLi) {
      triggerLi = checkboxes[0]?.closest('li');
    }

    if (triggerLi) {
      triggerLi.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  const syncToSlider = () => {
    if (inputMin && inputMax) slider.set([inputMin.value, inputMax.value]);
  };

  if (inputMin) inputMin.addEventListener('change', syncToSlider);
  if (inputMax) inputMax.addEventListener('change', syncToSlider);
}

// ── TopbarFilters class ───────────────────────────────────────────────────────

class TopbarFilters {
  constructor(container) {
    this.container = container;
    this.init();
  }

  init() {
    this.bindFilterDropdowns();
    this.bindSortDropdown();
    this.bindOutsideClick();
    this.initPriceRanges();
    this.initNumericRanges();
  }

  // ── Filter pill dropdowns ──────────────────────────────────────────────────

  bindFilterDropdowns() {
    this.container.querySelectorAll('[data-topbar-filter]').forEach(filter => {
      const btn = filter.querySelector('.topbar-filter__btn');
      const panel = filter.querySelector('.topbar-filter__panel');
      if (!btn || !panel) return;

      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.togglePanel(btn, panel);
      });

      panel.addEventListener('click', e => e.stopPropagation());
    });
  }

  togglePanel(btn, panel) {
    const isOpen = !panel.hidden;
    this.closeAll();
    if (!isOpen) {
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  closeAll() {
    this.container.querySelectorAll('.topbar-filter__panel, .topbar-sort__panel').forEach(panel => {
      if (!panel.hidden) {
        panel.hidden = true;
        const trigger = panel.closest('[data-topbar-filter], [data-topbar-sort]')
          ?.querySelector('[aria-controls]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  bindOutsideClick() {
    this._outsideHandler = () => this.closeAll();
    document.addEventListener('click', this._outsideHandler);
  }

  // ── Sort dropdown ──────────────────────────────────────────────────────────

  bindSortDropdown() {
    const sortEl = this.container.querySelector('[data-topbar-sort]');
    if (!sortEl) return;

    const btn = sortEl.querySelector('.topbar-sort__btn');
    const panel = sortEl.querySelector('.topbar-sort__panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', e => {
      e.stopPropagation();
      const isOpen = !panel.hidden;
      this.closeAll();
      if (!isOpen) {
        panel.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }
    });

    panel.addEventListener('click', e => e.stopPropagation());

    panel.querySelectorAll('[data-sort-value]').forEach(opt => {
      opt.addEventListener('click', () => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', opt.dataset.sortValue);
        params.delete('page');
        window.location.search = params.toString();
      });
    });
  }

  // ── noUiSlider: topbar price range (NOT .price-range, avoids double-init) ─

  initPriceRanges() {
    this.container.querySelectorAll('.topbar-price-range').forEach(el => {
      this.createPriceRange(el);
    });
  }

  createPriceRange(container) {
    if (typeof noUiSlider === 'undefined') {
      debug.warn('noUiSlider not available');
      return;
    }

    const sliderEl = container.querySelector('.topbar-price-range__slider');
    const inputMin = container.querySelector('.topbar-price-range__input--min');
    const inputMax = container.querySelector('.topbar-price-range__input--max');
    const displayMin = container.querySelector('.topbar-price-range__display-min');
    const displayMax = container.querySelector('.topbar-price-range__display-max');
    const hiddenMin = container.querySelector('.topbar-price-range__hidden-min');
    const hiddenMax = container.querySelector('.topbar-price-range__hidden-max');

    if (!sliderEl) return;

    const min = parseFloat(container.dataset.min) || 0;
    const max = parseFloat(container.dataset.max) || 100;
    const minValue = parseFloat(container.dataset.minValue) || min;
    const maxValue = parseFloat(container.dataset.maxValue) || max;

    if (sliderEl.noUiSlider) sliderEl.noUiSlider.destroy();

    const slider = noUiSlider.create(sliderEl, {
      connect: true,
      step: 10,
      start: [minValue, maxValue],
      range: { min, max },
    });

    slider.on('update', ([low, high]) => {
      if (inputMin) inputMin.value = Math.round(low);
      if (inputMax) inputMax.value = Math.round(high);

      const fmt = window.theme?.settings?.moneyFormat;
      if (displayMin && window.theme?.Currency?.formatMoney && fmt) {
        displayMin.innerHTML = theme.Currency.formatMoney(low, fmt);
      }
      if (displayMax && window.theme?.Currency?.formatMoney && fmt) {
        displayMax.innerHTML = theme.Currency.formatMoney(high, fmt);
      }
    });

    slider.on('change', ([low, high]) => {
      if (hiddenMin) hiddenMin.value = Math.round(low);
      if (hiddenMax) hiddenMax.value = Math.round(high);
      const triggerLi = container.querySelector('.topbar-price-range__trigger-li');
      if (triggerLi) {
        triggerLi.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    const syncToSlider = () => {
      if (inputMin && inputMax) slider.set([inputMin.value, inputMax.value]);
    };

    if (inputMin) inputMin.addEventListener('change', syncToSlider);
    if (inputMax) inputMax.addEventListener('change', syncToSlider);
  }

  // ── noUiSlider: numeric list range (backed by hidden checkboxes) ───────────

  initNumericRanges() {
    this.container.querySelectorAll('.numeric-range').forEach(el => {
      createNumericRange(el);
    });
  }

  destroy() {
    document.removeEventListener('click', this._outsideHandler);
  }
}

// ── Lifecycle ──────────────────────────────────────────────────────────────

function initTopbarFilters() {
  document.querySelectorAll('[data-topbar-filters]').forEach(el => {
    if (el._topbarFilters) {
      el._topbarFilters.destroy();
    }
    el._topbarFilters = new TopbarFilters(el);
    debug.log('initialized', el);
  });
}

// Init numeric range sliders outside the topbar (e.g. filter drawer)
function initDrawerNumericRanges() {
  document.querySelectorAll('.numeric-range').forEach(el => {
    if (!el.closest('[data-topbar-filters]')) {
      createNumericRange(el);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initTopbarFilters();
  initDrawerNumericRanges();
});
document.addEventListener('collection:reloaded', () => {
  initTopbarFilters();
  initDrawerNumericRanges();
});
